import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { gql, useMutation, useQuery } from '@apollo/client';
import { CurrentMerchantForConsentQuery, SetConsentMutation } from '@/gql/graphql';
import { Logo } from '../../components/logos/logo';
import { ArrowLeft } from 'lucide-react';

const CURRENT_MERCHANT_QUERY = gql`
  query CurrentMerchantForConsent {
    currentMerchant {
      id
      hasConsentEnabled
    }
  }
`;

const SET_CONSENT_MUTATION = gql`
  mutation SetConsent($consent: Boolean!) {
    setConsent(consent: $consent) {
      id
      hasConsentEnabled
    }
  }
`;

const formSchema = z.object({
  hasConsentBanner: z.enum(['yes', 'no'])
});

interface ConsentProps {
  /**
   * Text to display on the submit button.
   * Defaults to 'Save and Continue'.
   */
  buttonText?: string;

  /**
   * Custom handler to execute upon successful form submission.
   * Optional. If not provided, defaults to marking the step as completed and navigating.
   */
  onSubmitSuccess?: () => void;

  /**
   * Controls the visibility of the footer section containing the submit button.
   * Defaults to true.
   */
  showFooter?: boolean;

  /**
   * Text for the header of the consent form.
   * Defaults to 'Does your website have a consent banner?'
   */
  headerText?: string;
}

export function Consent({
  buttonText = 'Save and Continue',
  onSubmitSuccess,
  showFooter = true,
  headerText = 'Does your website have a consent banner?'
}: ConsentProps) {
  const navigate = useNavigate();
  const { data: currentMerchantData } =
    useQuery<CurrentMerchantForConsentQuery>(CURRENT_MERCHANT_QUERY);
  const [setConsent] = useMutation<SetConsentMutation>(SET_CONSENT_MUTATION);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      hasConsentBanner: currentMerchantData?.currentMerchant.hasConsentEnabled ? 'yes' : 'no'
    }
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    void setConsent({
      variables: {
        consent: values.hasConsentBanner === 'yes'
      }
    }).then(() => {
      if (onSubmitSuccess) {
        onSubmitSuccess();
      } else {
        void navigate('../setup/configure-pixel');
      }
    });
  };

  return (
    <Card className="w-full max-w-xl mx-4 sm:mx-auto p-9 rounded-[1rem]">
      <ArrowLeft
        className="cursor-pointer"
        onClick={() => {
          void navigate(-1);
        }}
      />
      <Form {...form}>
        <form
          id="consent-form"
          onSubmit={(e) => {
            void form.handleSubmit(onSubmit)(e);
          }}
        >
          <CardHeader className="items-center space-y-8 p-0">
            <Logo icon="contextual" width={36} height={36} />
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-medium mb-8">
              Connect your store
            </h1>
            <div className="flex gap-6">
              <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-black text-white text-center font-bold flex items-center justify-center">
                1
              </div>
              <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-black text-white text-center font-bold flex items-center justify-center">
                2
              </div>
              <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-black text-white text-center font-bold flex items-center justify-center">
                3
              </div>
              <div className="sm:w-10 sm:h-10 w-8 h-8 rounded-full bg-gray-600 text-white text-center font-bold flex items-center justify-center">
                4
              </div>
            </div>
          </CardHeader>

          <CardContent className="my-9 p-0">
            <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold">
              {headerText}
            </h2>
            <p className="text-xs sm:text-xs md:text-sm lg:text-base text-gray-600 mt-2">
              If you are using a consent banner, Contextual can integrate with it to control events
              guarded by user consent.
            </p>
            <FormField
              control={form.control}
              name="hasConsentBanner"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-col space-y-1 mt-3"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="no" id="no" />
                        <Label htmlFor="no">No</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="yes" id="yes" />
                        <Label htmlFor="yes">Yes</Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
          </CardContent>
          {showFooter && (
            <CardFooter className="flex justify-center space-x-8">
              <Button type="submit" form={'consent-form'} disabled={!form.formState.isValid}>
                {buttonText}
              </Button>
              <Button variant="secondary" className="">
                Cancel
              </Button>
            </CardFooter>
          )}
        </form>
      </Form>
    </Card>
  );
}
